import React, { useMemo } from 'react';
import shallow from 'zustand/shallow';
import { useTranslation } from 'react-i18next';

import {
  DataTableEditCell,
  DataTableEditCellSizes,
  DatatableEditCellVariant,
  Locale,
  QuickEditListItemProps,
  SelectOption,
} from '@evacenter/eden';

import type { NativeSelectOption, NativeSelectProps } from '@eva-pacs/ui';
import { useFeatureFlagsStore, useSessionStore } from '~/src/store';
import { CAN_USE_WORKLIST, FeatureFlags, POLICIES } from '~/constants';

import { useLanguage } from '~/src/hooks/useLanguage';

import { useGetAssignablePractitionerOptions } from '~/src/hooks/practitioners/useGetAssignablePractitionerOptions';

interface PractitionerCellProps extends Omit<NativeSelectProps, 'options' | 'onChange'> {
  fallbackOption: NativeSelectOption;
  onChange?: (option: SelectOption) => void;
  isStudySigned?: boolean;
}

/**
 * Practitioner Cell (Table)
 * @author Alejandro Forero<alejandro.forero@edenmed.com>
 * Created at 2024-01-31
 */
export const PractitionerCell = ({
  fallbackOption,
  isStudySigned,
  onChange,
  isDisabled,
  value,
}: PractitionerCellProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const { options, loading } = useGetAssignablePractitionerOptions({
    fetchPolicy: 'cache-first',
  });

  const hasFlag = useFeatureFlagsStore((store) => store.hasFlag);
  const denyAssign = (!hasFlag(FeatureFlags.EnablePractitionerReassignment) && isStudySigned) || isDisabled || loading;

  const [user, getCurrentAccount] = useSessionStore((store) => [store.user, store.getCurrentAccount], shallow);

  const UNASSIGNED_PRACTITIONER = {
    label: t('admin.permission.practitioners.options.unassigned'),
    value: '',
  };
  const fullPractitionerOptions: Array<NativeSelectOption> = useMemo(() => {
    const canUserBeAssigned = POLICIES[CAN_USE_WORKLIST](user);

    if (canUserBeAssigned) {
      let currentUserOption: SelectOption | undefined = undefined;

      const currentOptions = options.filter((option) => {
        if (option.value === getCurrentAccount()?.practitionerId) {
          currentUserOption = option;
          return false;
        }

        return true;
      });

      if (!currentUserOption) return [UNASSIGNED_PRACTITIONER, ...options];

      // If the user is a radiologist, then assign its own practitionerId as the first option.
      return [currentUserOption, UNASSIGNED_PRACTITIONER, ...currentOptions];
    }

    return [UNASSIGNED_PRACTITIONER, ...options];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, t]);

  const getPrimaryText = () => {
    if (!fallbackOption.value) return t('study.filterFields.notAssigned');
    return fallbackOption.label;
  };

  const getCellVariant = () => {
    if (denyAssign) return DatatableEditCellVariant.ONLY_READ;

    return DatatableEditCellVariant.LOCAL;
  };

  const handleOptionChange = (option: SelectOption) => {
    if (onChange) onChange(option);
  };

  const formatListItems = (): Array<QuickEditListItemProps> => {
    return [
      {
        primaryText: getPrimaryText(),
        locale: currentLanguage as Locale,
        onOptionChange: handleOptionChange,
        isDisabled,
        selectValue: value || fallbackOption.value,
        options: options.length > 0 ? fullPractitionerOptions : options,
        cellVariant: getCellVariant(),
        size: DataTableEditCellSizes.xs,
        preloadOptions: value ? [fallbackOption] : [],
        blockBodyScroll: fullPractitionerOptions.length <= 7 && loading,
      },
    ];
  };

  return <DataTableEditCell listItems={formatListItems()} />;
};
